import axios from 'axios';
import {
  AiConversation,
  AiConversationSummary,
  AiScopeTarget,
} from '@shared/models/ai-chat.model';
import type { OpenAiAllowedModels } from '@shared/models/ai/ai-models.model';

type SendMessageBody = {
  scopeTarget: AiScopeTarget;
  text: string;
  conversationId?: string;
  /** Project bucket the conversation belongs to. `null` = organization scope. */
  projectId: number | null;
  /** Nodon-employee override; server rejects from non-admin users. */
  model?: OpenAiAllowedModels;
  files?: File[];
};

const sendMessage = (body: SendMessageBody) => {
  const { files, ...json } = body;
  if (!files?.length) {
    return axios.post<AiConversation>('/ai-chat', json).then((r) => r.data);
  }
  const form = new FormData();
  form.append('text', json.text);
  form.append('scopeTarget', JSON.stringify(json.scopeTarget));
  form.append(
    'projectId',
    json.projectId === null ? '' : String(json.projectId),
  );
  if (json.conversationId) form.append('conversationId', json.conversationId);
  if (json.model) form.append('model', json.model);
  for (const file of files) form.append('files', file, file.name);
  return axios
    .post<AiConversation>('/ai-chat', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => r.data);
};

type CurrentConversationResponse = { conversation: AiConversation };

const projectIdQuery = (projectId: number | null) =>
  projectId === null ? '' : `?projectId=${projectId}`;

export const aiChatApi = {
  listConversations: (projectId: number | null) =>
    axios
      .get<{
        conversations: AiConversationSummary[];
      }>(`/ai-chat${projectIdQuery(projectId)}`)
      .then((r) => r.data.conversations),

  sendMessage: (body: SendMessageBody) => sendMessage(body),

  /**
   * Fetch the live conversation for the caller + org + project (creates on
   * miss). `projectId === null` means organization-level chat (user not inside
   * any project).
   */
  getCurrentConversation: (projectId: number | null) =>
    axios
      .get<CurrentConversationResponse>(
        `/ai-chat/current${projectIdQuery(projectId)}`,
      )
      .then((r) => r.data.conversation),

  createConversation: (projectId: number | null) =>
    axios
      .post<AiConversation>('/ai-chat/conversations', { projectId })
      .then((r) => r.data),

  getConversation: (id: string) =>
    axios.get<AiConversation>(`/ai-chat/${id}`).then((r) => r.data),

  patchConversationTitle: (id: string, body: { title: string | null }) =>
    axios.patch<{ ok: true }>(`/ai-chat/${id}`, body).then((r) => r.data),

  deleteConversation: (id: string) =>
    axios.delete(`/ai-chat/${id}`).then((r) => r.data),

  cancelPendingMessages: (conversationId: string, messageId?: string) => {
    const url = messageId
      ? `/ai-chat/${conversationId}/pending/${messageId}`
      : `/ai-chat/${conversationId}/pending`;
    return axios.delete<{ cancelled: number }>(url).then((r) => r.data);
  },
};
